import "./FacilityUserEditComponent.scss";
import {Field, FieldProps, Formik, FormikProps} from "formik";
import React, {useCallback, useEffect, useRef, useState} from "react";
import Grid from "@mui/material/Grid";
import FormikSelectDropdownComponent
    from "../../../../../shared/components/core/formik-select-dropdown/FormikSelectDropdownComponent";
import {CommonService} from "../../../../../shared/services";
import ButtonComponent from "../../../../../shared/components/core/button/ButtonComponent";
import FeedbackComponent from "../../../../../shared/components/feedback/feedbackComponent";
import commonService from "../../../../../shared/services/common.service";
import * as Yup from "yup";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import TableWrapperV2Component
    from "../../../../../shared/components/table-components/table-wrapper-v2/TableWrapperV2Component";
import {APIConfig} from "../../../../../constants";
import {ITableColumn} from "../../../../../shared/models/table.model";
import ChipComponent from "../../../../../shared/components/chip/ChipComponent";
import TooltipComponent from "../../../../../shared/components/tooltip/tooltipComponent";
import AssignEntityUserLocationComponent
    from "../../../../../shared/components/assign-entity-user-location/AssignEntityUserLocationComponent";
import RouteConfigService from "../../../../../shared/services/route-config.service";
import {useLocation, useNavigate} from "react-router-dom";
import LinkComponent from "../../../../../shared/components/link/LinkComponent";

interface FacilityUserEditComponentProps {
    userDetailsList: any;
    facilityId?: any;
    onUpdateComplete: Function;
    onEditCancel: Function;
}

const facilityPhysicianDetailsValidationSchema = Yup.object({
    userId: Yup.string()
        .nullable(),
});


interface facilityPhysicianDetailsFormSchema {
    userId: string;
}

const facilityInformationInitialValues: facilityPhysicianDetailsFormSchema = {
    userId: "",
}

const MODULE_NAME = "facilityUserList";
const modalInitialConfig = {isOpen: false, userId: undefined, locationDetails: []}
const FacilityUserEditComponent = (props: FacilityUserEditComponentProps) => {
    const {userDetailsList, facilityId, onEditCancel} = props;
    const location = useLocation();
    const navigate = useNavigate();
    const [userList, setUserList] = useState<any>([]);
    const [userListDropdown, setUserListDropdown] = useState<any>([]);
    const formikRef = useRef<FormikProps<any>>(null);
    const [modalConfig, setModalConfig] = useState(modalInitialConfig)

    const columns: ITableColumn[] = [
        {
            id: 'id',
            label: 'ID',
            isDefaultColumn: true,
            fixed: 'left',
            width: '150',
            content: (item: any) => (
                <>
                    {item?.userDetails?.id || '-'}
                </>
            )
        },
        {
            id: 'userName',
            label: 'User Name',
            isDefaultColumn: true,
            content: (item: any) => (
                <>
                    {commonService.generateNameFromNameObject(item?.userDetails) || '-'}
                </>
            )
        },
        {
            id: 'gender',
            label: 'Gender',
            isDefaultColumn: false,
            align: 'center',
            content: (item: any) => (
                <>
                    {item?.userDetails?.gender || '-'}
                </>
            )

        },
        {
            id: 'mobileNumber', label: 'Mobile Number', isDefaultColumn: true, align: 'center',
            content: (item: any) => (
                item?.userDetails?.mobileNumber ?
                    <>
                        {CommonService.getMaskedMobileNumber(item?.userDetails?.mobileNumber)}
                    </> : <>-</>
            )
        },
        {
            id: 'emailId',
            label: 'Email ID',
            isDefaultColumn: true,
            align: 'left',
            width: '250',
            content: (item: any) => (
                <>
                    {item?.userDetails?.emailId || '-'}
                </>
            )
        },
        {
            id: 'role',
            label: 'Role',
            isDefaultColumn: true,
            width: '150',
            content: (item: any) => (
                <>
                    {item?.userDetails?.roleObj?.title || '-'}
                </>
            )
        },
        {
            id: 'location',
            label: 'Assigned Location',
            isDefaultColumn: true,
            width: '150',
            content: (item: any) => {
                const locationLists = item?.locationDetails?.map((location: any) => location.name) || [];

                const chips = locationLists.map((locationName: string) => (
                    <ChipComponent key={locationName} label={locationName}/>
                ));

                return (
                    <>
                        {locationLists.length > 0 ? <TooltipComponent arrow={true} placement={"top"} tooltip={chips}>
                            <div
                                className={"ellipses-text"}>{locationLists.map((locationName: string) => locationName).join(",")}</div>
                        </TooltipComponent> : "-"}
                    </>
                );
            }
        },
        {
            id: "assign_location",
            label: 'Assign Location',
            isDefaultColumn: false,
            align: 'center',
            width: '150',
            content: (item: any) => (
                <>
                    <ButtonComponent variant={"text"} color={"primary"}
                                     handleClick={() => {
                                         setModalConfig((prev) => {
                                             return {
                                                 ...prev,
                                                 isOpen: true,
                                                 userId: item?.userDetails?.id,
                                                 locationDetails: item?.locationDetails || []
                                             }
                                         })
                                     }}
                    >
                        Assign Location
                    </ButtonComponent>
                </>
            )
        },
        {
            id: "Remove User",
            label: "Un-link User",
            align: "center",
            fixed: "right",
            width: 150,
            content: (item: any, index: number) => {
                return (
                    userList[index]?.roleObj?.title === "Facility Admin" ?
                        <></> :
                        <ButtonComponent
                            startIcon={<RemoveCircleIcon/>}
                            variant={"outlined"}
                            fullWidth={false}
                            size={'small'}
                            type={'button'}
                            color={"primary"}
                            handleClick={
                                () => {
                                    handleRemoveUser(item?.userDetails)
                                }
                            }

                        >
                            Remove
                        </ButtonComponent>
                )
            }
        }
    ];

    const handleUpdateUserLocation = useCallback((values: any) => {
        if (modalConfig.userId) {
            CommonService._facility.updateFacilityUserLocation(facilityId, modalConfig.userId, values).then((res) => {
                CommonService._communications.TableWrapperRefreshSubject.next({
                    moduleName: MODULE_NAME
                });
                CommonService._alert.showToast("Location updated successfully")
                setModalConfig(modalInitialConfig)
            })
        }
    }, [facilityId, modalConfig.userId])

    const onHandleNext = useCallback(() => {
        onEditCancel(userList);
    }, [onEditCancel, userList]);

    const getUserListDropdown = useCallback(() => {
        CommonService._facility.getFacilityUserDetails().then((response) => {
            setUserListDropdown(response.data);
        }).catch((error) => {
            console.error("Unable to get facility user", error)
        })
    }, [])

    const handleAddUser = useCallback((userId: string | number, facilityId: string | number) => {
        const payload = {
            facilityId: facilityId,
            userId: userId
        }
        formikRef?.current?.setSubmitting(true);
        CommonService._facility.addFacilityUserDetails(payload).then((response) => {
            const selectedUser = userListDropdown.find((user: any) => user?.id?.toString() === userId?.toString());
            if (selectedUser) {
                setUserList((prev: any) => {
                    return [...prev, selectedUser]
                })
                if (formikRef) {
                    formikRef?.current?.setFieldValue("userId", "");
                    formikRef?.current?.setSubmitting(false);
                }
                CommonService._communications.TableWrapperRefreshSubject.next({
                    moduleName: MODULE_NAME
                });
            }
        }).catch((error) => {
            CommonService._alert.showToast(error.message || "Error while adding user")
        })
    }, [userListDropdown])

    const handleRemoveUser = useCallback((user: any) => {
        const payload = {
            facilityId: facilityId,
            userId: user?.id
        }
        CommonService._facility.removeFacilityAssignedUser(payload).then((response) => {
            const userId = user?.id;
            setUserList((prev: any) => {
                return prev.filter((user: any) => user?.id?.toString() !== userId?.toString());
            })
            CommonService._communications.TableWrapperRefreshSubject.next({
                moduleName: MODULE_NAME
            });
            getUserListDropdown();
            CommonService._alert.showToast(response.message || "Removed user successfully")
        }).catch((error) => {
            CommonService._alert.showToast(error.message || "Error while removing user")
        })
    }, [facilityId, getUserListDropdown])

    const handleRedirectUserModule = useCallback(() => {
        navigate(RouteConfigService.AddUserRoute() + CommonService.setUrlSearchParams({
            returnUrl: location.pathname + location.search,
            permissionType: "facilityUser",
        }))
    }, [location.pathname, location.search, navigate])

    useEffect(() => {
        setUserList(userDetailsList || []);
    }, [userDetailsList]);

    useEffect(() => {
        getUserListDropdown();
    }, [getUserListDropdown]);
    return (
        <div className={'facility-user-edit-component'}>
            <AssignEntityUserLocationComponent
                isOpen={modalConfig.isOpen}
                entityId={facilityId}
                entityType={"facilityId"}
                onUpdate={handleUpdateUserLocation}
                assignedLocation={modalConfig.locationDetails || []}
                onClose={() => setModalConfig(modalInitialConfig)}/>
            <div className={'no-form'}>
                <div className="t-form-controls-wrapper">
                    <>
                        <Formik
                            validationSchema={facilityPhysicianDetailsValidationSchema}
                            initialValues={facilityInformationInitialValues}
                            validateOnChange={false}
                            validateOnBlur={true}
                            innerRef={formikRef}
                            enableReinitialize={true}
                            validateOnMount={true}
                            onSubmit={() => {
                            }}
                        >
                            {({isSubmitting, values, isValid, setFieldValue, validateForm, resetForm}) => {
                                // eslint-disable-next-line react-hooks/rules-of-hooks
                                useEffect(() => {
                                    validateForm();
                                }, [validateForm, values]);
                                return (
                                    <>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Field name={'userId'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikSelectDropdownComponent
                                                                label={'User'}
                                                                options={CommonService.getFilteredUsersDropdownList(userList, userListDropdown)}
                                                                placeholder={'Select User'}
                                                                // valueKey={'code'}
                                                                valueKey={'id'}
                                                                // required={true}
                                                                getOptionLabel={(option) => (`${option.firstName} ${option.lastName}`) || ''}
                                                                formikField={field}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                                <div className="redirect-new-link-wrapper">
                                                    <div>Didn't find user?</div>
                                                    <LinkComponent className={"redirect-link"}
                                                                   onClick={handleRedirectUserModule}>Create
                                                        User</LinkComponent>
                                                </div>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <ButtonComponent
                                                    className="mrg-left-20 mrg-top-5"
                                                    disabled={!isValid || isSubmitting || values.userId === ''}
                                                    color={"secondary"}
                                                    variant={"outlined"}
                                                    fullWidth={false}
                                                    size={'large'}
                                                    // isLoading={isGetPhysicianDetailsLoading}
                                                    handleClick={() => {
                                                        // getNpiDetails(values.userId, resetForm)
                                                        facilityId && handleAddUser(values.userId, facilityId)
                                                    }}>
                                                    Assign User
                                                </ButtonComponent>
                                            </Grid>
                                        </Grid>
                                    </>
                                )
                            }
                            }
                        </Formik>
                        <div className={'form-sub-title mrg-top-20 mrg-bottom-20'}>Added Users</div>
                        {!userList?.length ?
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <FeedbackComponent>
                                        No user have been added.
                                    </FeedbackComponent>
                                </Grid>
                            </Grid> :
                            // <>
                            //     {userList?.map((facilityUser: any, index: any) => {
                            //             return (
                            //                 <div key={index} className="accordion-layout mrg-bottom-20">
                            //                     <AccordionComponent
                            //                         isExpanded={userList[index]?.isFacilityUserExpand}
                            //                         title={`${CommonService.capitalizeFirstLetter(facilityUser.firstName)} ${CommonService.capitalizeFirstLetter(facilityUser.middleName)} ${CommonService.capitalizeFirstLetter(facilityUser.lastName)}`}
                            //                         subTitle={`(User ID: ${facilityUser?.id} | ${userList[index]?.roleObj?.title || ""})`}
                            //                         actions={
                            //                             userList[index]?.roleObj?.title === "Facility Admin" ?
                            //                                 <></> :
                            //                                 <ButtonComponent
                            //                                     startIcon={<RemoveCircleIcon/>}
                            //                                     variant={"outlined"}
                            //                                     fullWidth={false}
                            //                                     size={'small'}
                            //                                     type={'button'}
                            //                                     color={"primary"}
                            //                                     handleClick={
                            //                                         () => {
                            //                                             handleRemoveUser(facilityUser)
                            //                                         }
                            //                                     }
                            //
                            //                                 >
                            //                                     Remove
                            //                                 </ButtonComponent>}
                            //                     >
                            //                         <div className="facilityUser-details-section">
                            //                             <>
                            //                                 <div className="view-and-edit-title">
                            //                                     Personal Details
                            //                                 </div>
                            //                                 <div className="details-tab-data-body">
                            //                                     <Grid container spacing={2} className="pdd-top-0">
                            //                                         <Grid item xs={12} sm={12} md={6} xl={6}>
                            //                                             <DetailsDataKeyValueComponent
                            //                                                 title={"First Name"}> {commonService.capitalizeFirstLetterOfEachWord(facilityUser?.firstName) || 'N/A'} </DetailsDataKeyValueComponent>
                            //                                             <DetailsDataKeyValueComponent
                            //                                                 title={"Last Name"}> {commonService.capitalizeFirstLetterOfEachWord(facilityUser?.lastName) || 'N/A'} </DetailsDataKeyValueComponent>
                            //                                             <DetailsDataKeyValueComponent
                            //                                                 title={"Gender"}> {facilityUser?.gender || 'N/A'} </DetailsDataKeyValueComponent>
                            //                                         </Grid>
                            //                                         <Grid item xs={12} sm={12} md={6} xl={6}>
                            //                                             <DetailsDataKeyValueComponent
                            //                                                 title={"Middle Name"}> {commonService.capitalizeFirstLetterOfEachWord(facilityUser?.middleName) || 'N/A'} </DetailsDataKeyValueComponent>
                            //                                             <DetailsDataKeyValueComponent
                            //                                                 title={"Date of Birth"}> {commonService.formatDate(facilityUser?.dateOfBirth) || 'N/A'} </DetailsDataKeyValueComponent>
                            //                                         </Grid>
                            //                                     </Grid>
                            //                                     <div className="horizontal-line"/>
                            //                                     <div className="view-and-edit-title">Contact
                            //                                         Details
                            //                                     </div>
                            //                                     <div className="details-tab-data-body">
                            //                                         <Grid container spacing={2}>
                            //                                             <Grid item xs={12} sm={12} md={6} xl={6}>
                            //                                                 <DetailsDataKeyValueComponent
                            //                                                     title={"Email ID"}> {facilityUser?.emailId || 'N/A'} </DetailsDataKeyValueComponent>
                            //                                             </Grid>
                            //                                             <Grid item xs={12} sm={12} md={6} xl={6}>
                            //                                                 <DetailsDataKeyValueComponent
                            //                                                     title={"Mobile Number"}> {CommonService.getMaskedMobileNumber(facilityUser?.mobileNumber) || 'N/A'} </DetailsDataKeyValueComponent>
                            //                                             </Grid>
                            //                                             <Grid item xs={12} sm={12} md={6} xl={6}>
                            //                                                 <DetailsDataKeyValueComponent
                            //                                                     title={"Role"}> {facilityUser?.role?.title || 'N/A'} </DetailsDataKeyValueComponent>
                            //                                             </Grid>
                            //                                         </Grid>
                            //                                     </div>
                            //                                     {/*{*/}
                            //                                     {/*    ["location", "facility", "lab"].map(key => (*/}
                            //                                     {/*        labUser[key] && (*/}
                            //                                     {/*            <>*/}
                            //                                     {/*                <div className="horizontal-line"/>*/}
                            //                                     {/*                <div*/}
                            //                                     {/*                    className="view-and-edit-title">Linked*/}
                            //                                     {/*                    Details*/}
                            //                                     {/*                </div>*/}
                            //                                     {/*                <div*/}
                            //                                     {/*                    className="details-tab-data-body">*/}
                            //                                     {/*                    <Grid container spacing={2}>*/}
                            //                                     {/*                        <Grid item xs={12} sm={12}*/}
                            //                                     {/*                              md={6} xl={6}>*/}
                            //                                     {/*                            <DetailsDataKeyValueComponent*/}
                            //                                     {/*                                title={key.charAt(0).toUpperCase() + key.slice(1)}> {labUser[key].name || 'N/A'} </DetailsDataKeyValueComponent>*/}
                            //                                     {/*                        </Grid>*/}
                            //                                     {/*                    </Grid>*/}
                            //                                     {/*                </div>*/}
                            //                                     {/*            </>*/}
                            //                                     {/*        )*/}
                            //                                     {/*    ))*/}
                            //                                     {/*}*/}
                            //                                 </div>
                            //                             </>
                            //                         </div>
                            //                     </AccordionComponent>
                            //                 </div>
                            //             )
                            //         }
                            //     )}
                            // </>
                            <div className="table-list-container">
                                <TableWrapperV2Component
                                    isPaginated={false}
                                    isColumnFilterVisible={false}
                                    columns={columns}
                                    url={APIConfig.GET_FACILITY_ASSIGNED_USER_LIST.URL(facilityId)}
                                    method={APIConfig.GET_FACILITY_ASSIGNED_USER_LIST.METHOD}
                                    // extraPayload={{}}
                                    moduleName={MODULE_NAME}
                                />
                            </div>
                        }
                    </>
                </div>
                <div className='form-action-section-edit'>
                    {/*<ButtonComponent*/}
                    {/*    type="button"*/}
                    {/*    color={"primary"}*/}
                    {/*    variant={"outlined"}*/}
                    {/*    // disabled={formikRef?.current?.isSubmitting}*/}
                    {/*    fullWidth={true}*/}
                    {/*    handleClick={onEditCancel}*/}
                    {/*>*/}
                    {/*    Close*/}
                    {/*</ButtonComponent>*/}
                    <ButtonComponent
                        color={"primary"}
                        type="button"
                        variant={"contained"}
                        className={"width-25"}
                        // disabled={formikRef?.current?.isSubmitting}
                        // fullWidth={true}
                        handleClick={onHandleNext}
                    >
                        Done
                    </ButtonComponent>
                </div>
            </div>
        </div>
    );

};

export default FacilityUserEditComponent;
